export interface PrayerTimeEntry {
  name: 'Fajr' | 'Dhuhr' | 'Asr' | 'Maghrib' | 'Isha';
  /** Localized display string (e.g. "5:30 AM", "٥:٣٠ ص") — for the UI only. */
  time: string;
  /**
   * Machine-readable minutes since midnight, derived from the raw 24h API value.
   * Always locale-independent so proximity logic (next-prayer countdown) never has
   * to parse the localized `time` string, whose digits/markers vary by locale.
   */
  minutes: number;
}

interface AladhanResponse {
  code: number;
  data?: {
    timings?: Record<string, string>;
  };
}

const DEFAULT_METHOD = Number(process.env.REACT_APP_PRAYER_CALC_METHOD || 2);

// Parse the raw 24h API value ("17:30" / "05:30 (EET)") into hour/minute. Returns
// null when the value is malformed so callers can fall back gracefully.
// Exported: prayerSchedule.ts parses the same Aladhan timing strings.
export const parse24h = (time24: string): { hh: number; mm: number } | null => {
  const clean = (time24 || '').split(' ')[0].trim();
  const [hh, mm] = clean.split(':').map(Number);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
  return { hh, mm };
};

// Exported: prayerSchedule.ts localizes notification-body times the same way.
export const to12h = (time24: string, locale?: string): string => {
  const parsed = parse24h(time24);
  if (!parsed) return time24;
  const { hh, mm } = parsed;
  // Localize the time (AM/PM markers become ص/م in Arabic, digits localize, etc.).
  try {
    const d = new Date(2000, 0, 1, hh, mm);
    return new Intl.DateTimeFormat(locale || undefined, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(d);
  } catch {
    const period = hh >= 12 ? 'PM' : 'AM';
    const h12 = hh % 12 || 12;
    return `${h12}:${String(mm).padStart(2, '0')} ${period}`;
  }
};

// Minutes since midnight from the raw 24h API value. Locale-independent — this is
// what proximity logic consumes, never the localized display string.
const toMinutes = (time24: string): number => {
  const parsed = parse24h(time24);
  return parsed ? parsed.hh * 60 + parsed.mm : 0;
};

const entry = (
  name: PrayerTimeEntry['name'],
  time24: string,
  locale?: string,
): PrayerTimeEntry => ({ name, time: to12h(time24, locale), minutes: toMinutes(time24) });

export const fetchPrayerTimes = async (
  latitude: number,
  longitude: number,
  method = DEFAULT_METHOD,
  locale?: string,
): Promise<PrayerTimeEntry[]> => {
  const ts = Math.floor(Date.now() / 1000);
  const safeMethod = Math.max(1, Math.min(20, Math.trunc(method || DEFAULT_METHOD)));
  const url = `https://api.aladhan.com/v1/timings/${ts}?latitude=${latitude}&longitude=${longitude}&method=${safeMethod}`;

  const res = await fetch(url);
  if (!res.ok) {
    throw new Error('Failed to fetch prayer times');
  }

  const json = (await res.json()) as AladhanResponse;
  const timings = json?.data?.timings;
  if (!timings) {
    throw new Error('Prayer times response is missing timings');
  }

  return [
    entry('Fajr', timings.Fajr, locale),
    entry('Dhuhr', timings.Dhuhr, locale),
    entry('Asr', timings.Asr, locale),
    entry('Maghrib', timings.Maghrib, locale),
    entry('Isha', timings.Isha, locale),
  ];
};
